import type { UseQueryResult } from "@tanstack/react-query";
import { Card } from "~/components/Card";
import { DetailsLayout, LockCard } from "~/components/DetailsCards";
import type { DigestionPart } from "~/lqs";
import {
  LqsHistoryCard,
  LqsManageCard,
  LqsResourceFields,
  useDeleteDigestionPart,
  useDigestionPart,
  useUpdateDigestionPart,
} from "~/lqs";
import {
  calculateProcessRefetchInterval,
  Process,
} from "~/lqs/components/process";
import {
  makeDigestionPartsLocation,
  makeEditDigestionPartLocation,
  useDigestionPartParams,
} from "~/paths";
import { selectData } from "~/utils";

export function DigestionPartDetails() {
  const { digestionId, digestionPartId } = useDigestionPartParams();

  const query = useDigestionPart(digestionId, digestionPartId, {
    select: selectData,
    refetchInterval: calculateProcessRefetchInterval,
  });

  const generalSection = <GeneralSection query={query} />;
  const processSection = (
    <Process
      query={query}
      mutation={useUpdateDigestionPart(digestionId, digestionPartId)}
    />
  );
  const lockSection = (
    <LockCard
      resourceName="digestion part"
      query={query}
      updateMutation={useUpdateDigestionPart(digestionId, digestionPartId)}
    />
  );
  const historySection = <LqsHistoryCard query={query} />;
  const manageSection = (
    <LqsManageCard
      resourceName="digestion part"
      query={query}
      editLocation={makeEditDigestionPartLocation({
        digestionId,
        digestionPartId,
      })}
      deleteMutation={useDeleteDigestionPart(digestionId, digestionPartId)}
      getReadableName={(digestionPart) => digestionPart.id}
      listLocation={makeDigestionPartsLocation({ digestionId })}
    />
  );

  return (
    <DetailsLayout
      primaryGridColumn={
        <>
          {generalSection}
          {processSection}
        </>
      }
      secondaryGridColumn={
        <>
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
      stack={
        <>
          {generalSection}
          {processSection}
          {lockSection}
          {historySection}
          {manageSection}
        </>
      }
    />
  );
}

function GeneralSection({ query }: { query: UseQueryResult<DigestionPart> }) {
  return (
    <Card title="General">
      <LqsResourceFields
        query={query}
        fields={[
          { dataType: "id", accessor: "id" },
          {
            dataType: "foreign-key",
            resourceType: "digestion",
            accessor: "digestionId",
          },
          { dataType: "number", accessor: "sequence" },
        ]}
      />
    </Card>
  );
}
